import {HomepageTakeover} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/homepage_takeover.types';
import {useLocale} from '@aboutyou/i18n';
import {useHydrationDone} from '@aboutyou/lazy-imports';
import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {useVipLandingPageRoute} from '@aboutyou/router';
import {TrackBASMonitoring} from '@aboutyou/tracking';
import type {TransitionComponentType} from '@aboutyou/transitions';
import {AboutYouMainLogo, useTimedOut} from '@aboutyou/ui-components';
import {
  useCustomerIsVip,
  useUserFirstName,
  useUserGender,
} from '@aboutyou/user';
import React, {FC, useEffect, useState} from 'react';
import {BASCampaignHeaderLogo} from './BASCampaignHeaderLogo';
import {useBasCampaign} from './hooks/useBasCampaign';
import {useIsAYAnniversaryCampaignActive} from './hooks/useIsAYAnniversaryCampaignActive';
import {useIsTakeoverActive} from './hooks/useIsTakeoverActive';
import {useIsWoohooCampaignActive} from './hooks/useIsWoohooCampaignActive';

export interface AboutYouLogoProps {
  /**
   * set disableSvgLogo=true in places where we need to show the user's first name as ABOUT|NAME. Example in Offcanvas and Desktop Header
   * This will load the fonts needed for that
   */
  disableSvgLogo?: boolean;
  disableLoggedInUserUpdates?: boolean;
  disableShowUserName?: boolean;
  disableWoohoo?: boolean;
  disableLogoLink?: boolean;
  disableBasCampaignTakeover?: boolean;
  disableAYAnniversaryCampaign?: boolean;
  firstNameMaxLength?: number;
  className?: string;
  testId?: string;

  FirstNameAnimationComponent?: TransitionComponentType;

  trackOriginClickFunction?: () => void;
  homepageTakeover?: HomepageTakeover;
}

const BasHomePageTakeoverGlobalCSSSetter = ({
  isTakeoverActive,
}: {
  isTakeoverActive: boolean;
}) => {
  if (isTakeoverActive) {
    return null;
  }

  const innerHtml = function () {
    document.documentElement.style.setProperty(
      '--bas-takeover-icon-display',
      'none',
    );
  };

  return (
    <script
      data-testid="bas-homepage-takeover-global-css-setter"
      type="text/javascript"
      dangerouslySetInnerHTML={{
        __html: `(${innerHtml.toString()})()`,
      }}
    />
  );
};

// eslint-disable-next-line complexity
export const AboutYouLogo: FC<AboutYouLogoProps> = ({
  testId,
  disableSvgLogo = false,
  disableShowUserName = false,
  disableWoohoo = false,
  disableBasCampaignTakeover = false,
  disableAYAnniversaryCampaign = false,
  disableLogoLink = false,
  className,
  firstNameMaxLength,
  FirstNameAnimationComponent,
  disableLoggedInUserUpdates = false,
  trackOriginClickFunction,
  homepageTakeover,
}) => {
  const locale = useLocale();
  const isWoohooCampaignActive = useIsWoohooCampaignActive();
  const basCampaignTakeOver = useBasCampaign();
  const vipLandingPageRoute = useVipLandingPageRoute();
  const userGender = useUserGender();
  const incomingFirstName = useUserFirstName();
  const [firstName, setFirstName] = useState<string | undefined>(undefined);
  const incomingIsVip = useCustomerIsVip();
  const [isVip, setIsVip] = useState<boolean | undefined>(undefined);
  const isHeaderBasCampaignActive = useIsTakeoverActive({
    startsAt: homepageTakeover?.startsAt,
    endsAt: homepageTakeover?.endsAt,
  });

  /**
   * On desktop when a user login, we do a page reload.
   * We want to avoid updates to firstName and VIP logo to be rendered in the logo to avoid showing the logged-in user animation twice on desktop
   */
  const timedOut = useTimedOut(1000);
  const isHydrationDone = useHydrationDone();
  useEffect(() => {
    if (!isHydrationDone) return;

    if (disableLoggedInUserUpdates && timedOut) {
      // do not update the firstName and isVip state happening after later on in the lifecycle of the header
    } else {
      // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
      setIsVip(incomingIsVip);
      setFirstName(incomingFirstName);
    }
  }, [
    isHydrationDone,
    disableLoggedInUserUpdates,
    incomingFirstName,
    incomingIsVip,
    timedOut,
  ]);

  const showFirstName =
    !disableShowUserName &&
    firstName &&
    (disableBasCampaignTakeover ? true : !isHeaderBasCampaignActive);

  const isWoohooEnabled =
    !disableWoohoo &&
    isWoohooCampaignActive &&
    !isHeaderBasCampaignActive &&
    !isVip;

  const isVipShown =
    isVip && (disableBasCampaignTakeover ? true : !isHeaderBasCampaignActive);

  let BASCampaignHeaderLogoComponent;
  let ConditionalTrackBASMonitoring = null;
  if (disableBasCampaignTakeover) {
    BASCampaignHeaderLogoComponent = null;
  } else {
    if (
      isHeaderBasCampaignActive &&
      (homepageTakeover || basCampaignTakeOver)
    ) {
      BASCampaignHeaderLogoComponent = (
        <BASCampaignHeaderLogo
          targetUrl={homepageTakeover!.targetUrl.url}
          promotionId={homepageTakeover!.tracker.promotionId}
          promotionName={homepageTakeover!.tracker.promotionName}
          imagePath={homepageTakeover!.brandLogo.url}
          creativeName={homepageTakeover?.tracker.creativeName}
        />
      );
    } else {
      ConditionalTrackBASMonitoring = (
        <TrackBASMonitoring
          basPromotionSource={TrackingPromotionCreative.BasLogoIntegration}
        />
      );
    }
  }

  const vipLandingPageUrl = userGender
    ? `${vipLandingPageRoute}?gender=${userGender}`
    : vipLandingPageRoute;

  const isAYAnniversaryCampaignActive = useIsAYAnniversaryCampaignActive();

  return (
    <>
      {ConditionalTrackBASMonitoring}
      {homepageTakeover && (
        <BasHomePageTakeoverGlobalCSSSetter
          isTakeoverActive={isHeaderBasCampaignActive}
        />
      )}
      <AboutYouMainLogo
        id={testId ?? 'ay-logo'}
        testId={testId ?? 'ay-logo'}
        firstName={showFirstName ? firstName : undefined}
        isVip={Boolean(isVipShown)}
        vipLandingPageUrl={vipLandingPageUrl}
        BASCampaignHeaderLogoComponent={BASCampaignHeaderLogoComponent}
        woohooCampaign={
          isWoohooEnabled
            ? {
                isCampaignLive: isWoohooCampaignActive,
                locale,
              }
            : undefined
        }
        showAYAnniversaryCampaign={
          isAYAnniversaryCampaignActive && !disableAYAnniversaryCampaign
        }
        showHeartIcon={isAYAnniversaryCampaignActive}
        disableSvgLogo={disableSvgLogo}
        disableLogoLink={disableLogoLink}
        className={className}
        firstNameMaxLength={firstNameMaxLength}
        FirstNameAnimationComponent={FirstNameAnimationComponent}
        trackOriginClickFunction={trackOriginClickFunction}
      />
    </>
  );
};
